import React, { useEffect, useState } from "react";
import { FaStar, FaSearch, FaUsers } from "react-icons/fa";
import axiosInstance from "../../../../axios";

export default function GroupFeedbacksReport() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");

  useEffect(() => {
    const fetchFeedbacks = async () => {
      const token = JSON.parse(localStorage.getItem("token"));
      try {
        const res = await axiosInstance.get("/admin/group-feedbacks", {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.error) {
          setError(res.data.message || "Failed to load feedbacks.");
        } else {
          setFeedbacks(res.data.data || []);
        }
      } catch (err) {
        console.error(err);
        setError("Error loading feedbacks.");
      } finally {
        setLoading(false);
      }
    };
    fetchFeedbacks();
  }, []);

  const filteredFeedbacks = feedbacks.filter((fb) => {
    const matchesSearch = 
      fb.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fb.studentEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fb.courseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fb.batchName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fb.comments?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRating = ratingFilter === "all" || Number(fb.rating) === Number(ratingFilter);

    return matchesSearch && matchesRating;
  });

  const avgRating = feedbacks.length > 0 
    ? (feedbacks.reduce((acc, curr) => acc + curr.rating, 0) / feedbacks.length).toFixed(1)
    : 0;

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={`w-3.5 h-3.5 ${rating >= star ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 bg-[#f8fafc] min-h-screen">
      {/* Header */}
      <div className="mb-6 bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl p-6 shadow-md text-white">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FaUsers /> Group Batch Feedbacks
        </h1>
        <p className="text-slate-300 text-sm mt-1">
          Review overall student satisfaction, course ratings, and direct feedback from active cohorts.
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Total Feedbacks</span>
          <span className="text-3xl font-black text-slate-800 mt-1 block">{feedbacks.length}</span>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Average Cohort Rating</span>
          <span className="text-3xl font-black text-slate-800 mt-1 block flex items-center gap-1.5">
            {avgRating} <FaStar className="w-6 h-6 text-amber-400 fill-amber-400 inline" />
          </span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative flex-1 max-w-md w-full">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search feedback..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all"
          />
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto shrink-0">
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg bg-white text-slate-750 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer w-full md:w-auto"
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
        </div>
      </div>

      {/* Main Table */}
      {loading ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
          <p className="text-slate-500 font-semibold">Loading feedbacks...</p>
        </div>
      ) : error ? (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center text-rose-700 font-semibold shadow-sm">
          {error}
        </div>
      ) : filteredFeedbacks.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400 shadow-sm">
          No feedbacks found matching your search.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="py-4 px-6">Student</th>
                  <th className="py-4 px-4">Course / Batch</th>
                  <th className="py-4 px-4">Rating</th>
                  <th className="py-4 px-4">Comments</th>
                  <th className="py-4 px-6 text-right">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-750 text-sm">
                {filteredFeedbacks.map((fb) => (
                  <tr key={fb.id} className="hover:bg-slate-50/40 transition-all">
                    <td className="py-4 px-6">
                      <p className="font-bold text-slate-800">{fb.studentName}</p>
                      <p className="text-xs text-slate-450">{fb.studentEmail}</p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="font-semibold text-slate-700">{fb.courseName}</p>
                      <p className="text-xs text-slate-450 mt-0.5">{fb.batchName}</p>
                    </td>
                    <td className="py-4 px-4">
                      {renderStars(fb.rating)}
                    </td>
                    <td className="py-4 px-4 max-w-xs break-words">
                      <p className="text-slate-600 italic">"{fb.comments || "No comment left."}"</p>
                    </td>
                    <td className="py-4 px-6 text-right text-xs text-slate-450 font-semibold">
                      {new Date(fb.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
